import * as React from "react"
import { chakra } from "@chakra-ui/react"

/**
 * The "Book a Demo" CTA, used in the nav, hero, and homepage demo band. Unlike
 * GetStartedButton it does NOT run the sign-up/lead-form mode logic — the demo
 * is a plain navigation to the PM-facing product tour at /demo, so it stays a
 * simple link and never touches the lead funnel. `source` is passed through as
 * a data attribute for GTM click attribution.
 */
type Size = "sm" | "md" | "lg"

const SIZES: Record<Size, { px: string | number; py: string | number; fontSize: string }> = {
  sm: { px: 4, py: "7px", fontSize: "0.8rem" },
  md: { px: "28px", py: "12px", fontSize: "0.88rem" },
  lg: { px: "36px", py: "14px", fontSize: "0.95rem" },
}

interface DemoButtonProps {
  label?: string
  size?: Size
  variant?: "solid" | "outline"
  source?: string
  // Chakra style props to override per placement (e.g. w="100%").
  [key: string]: unknown
}

const DemoButton: React.FC<DemoButtonProps> = ({
  label = "Book a Demo",
  size = "md",
  variant = "solid",
  source = "demo",
  ...rest
}) => {
  const dims = SIZES[size]
  const solid = {
    color: "white",
    bg: "brand.400",
    border: "none",
    boxShadow: "0 4px 20px rgba(103,145,203,0.25)",
    _hover: {
      bg: "#7BA3D6",
      boxShadow: "0 8px 28px rgba(103,145,203,0.35)",
      transform: "translateY(-1px)",
    },
  }
  const outline = {
    color: "#1E2740",
    bg: "white",
    border: "1px solid #E2E6F2",
    _hover: { bg: "#F3F5FB", borderColor: "rgba(103,145,203,0.15)" },
  }
  const styles = variant === "solid" ? solid : outline

  return (
    <chakra.a
      href="/demo"
      data-cta-source={source}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap="8px"
      px={dims.px}
      py={dims.py}
      fontSize={dims.fontSize}
      fontWeight={600}
      borderRadius="full"
      textDecoration="none"
      cursor="pointer"
      transition="all 0.3s cubic-bezier(0.25,0.46,0.45,0.94)"
      {...styles}
      {...rest}
    >
      {label}
    </chakra.a>
  )
}

export default DemoButton
